import React from "react";
import { Box, Skeleton, Typography, IconButton } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { snapCenterToCursor } from "@dnd-kit/modifiers";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  UniqueIdentifier,
  pointerWithin,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import AddIcon from "@mui/icons-material/Add";

import { FlowTask } from "../../interfaces/flowTask.interface";
import { FlowTaskCard } from "../FlowTaskCard/FlowTaskCard";
import { TASK_STATUSES, STATUS_COLORS } from "../../constants";
import { STATUS_LABELS } from "../../StatusLabels/statusLabels";
import { useUpdateTodoTask } from "../../hooks/useUpdateTodoTask";
import { createPortal } from "react-dom";

interface KanbanBoardProps {
  tasks: FlowTask[];
  isLoading?: boolean;
  onCreateTask?: (status: string) => void;
  isPreviewMode?: boolean;
  isLimitReached?: boolean;
}

interface SortableTaskProps {
  task: FlowTask;
  isPreviewMode?: boolean;
}

interface DroppableColumnProps {
  status: string;
  tasks: FlowTask[];
  statusColor: (typeof STATUS_COLORS)[keyof typeof STATUS_COLORS];
  onCreateTask?: (status: string) => void;
  isPreviewMode?: boolean;
  isLimitReached?: boolean;
}

const Sx = {
  board: {
    bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
    display: "flex",
    gap: 2,
    overflowX: "auto",
    pb: 2,
    minHeight: "500px",
    width: "100%",
  },

  column: (isOver: boolean, statusColor: DroppableColumnProps["statusColor"]) => ({
    minWidth: "280px",
    flex: 1,
    bgcolor: isOver ? "var(--fx-surface-muted, #F1F5F9)" : "var(--fx-surface-sunken, #F8FAFC)",
    borderRadius: 2,
    p: 2,
    borderWidth: isOver ? "2px" : "1px",
    display: "flex",
    flexDirection: "column",
    transition: "all 0.2s ease",
  }),

  columnHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    mb: 2,
  },

  columnHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
  },

  columnTitle: (color: string) => ({
    fontWeight: 500,
    letterSpacing: "0.05em",
    color,
    textTransform: "uppercase",
    fontSize: "0.9rem",
  }),

  columnCount: (bg: string, color: string) => ({
    bgcolor: bg,
    color,
    px: 1.25,
    py: 0.5,
    borderRadius: 100,
    fontSize: "0.9rem",
    fontWeight: 600,
  }),

  addIconButton: (color: string) => ({
    color,
    opacity: 0.6,
    padding: "4px",
    "&:hover": {
      opacity: 1,
      bgcolor: "transparent",
    },
  }),

  tasksContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
    flex: 1,
    minHeight: "100px",
  },

  emptyState: {
    p: 3,
    textAlign: "center",
    color: "var(--fx-surface-texttertiary, #94A3B8)",
    fontSize: "0.875rem",
    border: "2px dashed var(--fx-surface-border, #E2E8F0)",
    borderRadius: 1,
  },

  overlay: {
    opacity: 0.95,
    cursor: "grabbing",

    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
    pointerEvents: "none",
    touchAction: "none",
  },

  loadingRow: { display: "flex", gap: 2 },
  loadingColumn: { flex: 1 },
  loadingHeaderSkeleton: { mb: 2, borderRadius: 1 },
  loadingCardSkeleton: { mb: 2, borderRadius: 1 },
};

const isTextInputFocused = () => {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;

  return (
    el.tagName === "INPUT" ||
    el.tagName === "TEXTAREA" ||
    (el.isContentEditable && el.isContentEditable) ||
    !!el.closest("input") ||
    !!el.closest("textarea")
  );
};

const getTasksByStatus = (tasks: FlowTask[]) => {
  return TASK_STATUSES.reduce(
    (acc, status) => {
      acc[status] = tasks.filter((t) => t.status === status);
      return acc;
    },
    {} as Record<string, FlowTask[]>,
  );
};

const shouldSyncOptimisticTasks = (optimistic: FlowTask[], incoming: FlowTask[]) => {
  if (optimistic === incoming) return false;
  if (optimistic.length !== incoming.length) return true;

  const optimisticIds = new Set(optimistic.map((t) => t.uid));
  const incomingIds = new Set(incoming.map((t) => t.uid));

  const sameIds = optimisticIds.size === incomingIds.size && Array.from(optimisticIds).every((id) => incomingIds.has(id));

  if (!sameIds) return true;

  const optimisticMap = new Map(optimistic.map((t) => [t.uid, t]));
  for (const task of incoming) {
    const opt = optimisticMap.get(task.uid);
    if (!opt || opt.status !== task.status || opt.name !== task.name || opt.priority !== task.priority) {
      return true;
    }
  }

  return false;
};

const getDropInfo = (overId: unknown, activeTask: FlowTask, allTasks: FlowTask[]): { newStatus?: FlowTask["status"]; isReordering: boolean } => {
  const asStatus = overId as FlowTask["status"];

  if (TASK_STATUSES.includes(asStatus)) {
    return { newStatus: asStatus, isReordering: false };
  }

  const overTask = allTasks.find((t) => t.uid === overId);
  if (overTask) {
    return {
      newStatus: overTask.status,
      isReordering: activeTask.status === overTask.status,
    };
  }

  return { newStatus: undefined, isReordering: false };
};

const SortableTask = ({ task, isPreviewMode }: SortableTaskProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.uid });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    cursor: isDragging ? "grabbing" : "grab",
  };

  const safeListeners = React.useMemo(
    () => ({
      ...listeners,
      onKeyDown: (event: React.KeyboardEvent) => {
        if (isTextInputFocused()) {
          event.stopPropagation();
          return;
        }
        listeners?.onKeyDown?.(event as any);
      },
    }),
    [listeners],
  );

  return (
    <div ref={setNodeRef} style={style} data-id={task.uid} {...attributes} {...safeListeners}>
      <FlowTaskCard task={task} isPreviewMode={isPreviewMode} />
    </div>
  );
};

const DroppableColumn = ({ status, tasks, statusColor, onCreateTask, isPreviewMode, isLimitReached }: DroppableColumnProps) => {
  const { setNodeRef, isOver } = useDroppable({
    id: status,
    data: { type: "column", status, accepts: ["task"] },
  });

  return (
    <Box ref={setNodeRef} sx={Sx.column(isOver, statusColor)}>
      <Box sx={Sx.columnHeader}>
        <Box sx={Sx.columnHeaderLeft}>
          <Typography variant="subtitle2" sx={Sx.columnTitle(statusColor.color)}>
            {STATUS_LABELS[status] ?? status.replace("_", " ")}
          </Typography>
          <Box sx={Sx.columnCount(statusColor.bg, statusColor.color)}>{tasks.length}</Box>
        </Box>

        {/* <IconButton
          size="small"
          onClick={() => onCreateTask?.(status)}
          sx={Sx.addIconButton(statusColor.color)}
        >
          <AddIcon fontSize="small" />
        </IconButton> */}
      </Box>

      <SortableContext items={tasks.map((t) => t.uid)} strategy={verticalListSortingStrategy}>
        <Box sx={Sx.tasksContainer}>
          {tasks.length > 0 ? (
            tasks.map((t) => <SortableTask key={t.uid} task={t} isPreviewMode={isPreviewMode} />)
          ) : (
            <Box sx={Sx.emptyState}>No tasks</Box>
          )}
        </Box>
      </SortableContext>
    </Box>
  );
};

const KanbanLoadingState = () => {
  return (
    <Box sx={Sx.loadingRow}>
      {TASK_STATUSES.map((status) => (
        <Box key={status} sx={Sx.loadingColumn}>
          <Skeleton variant="rectangular" height={40} sx={Sx.loadingHeaderSkeleton} />
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={120} sx={Sx.loadingCardSkeleton} />
          ))}
        </Box>
      ))}
    </Box>
  );
};

export const KanbanBoard = ({ tasks, isLoading, onCreateTask, isPreviewMode, isLimitReached }: KanbanBoardProps) => {
  const [activeId, setActiveId] = React.useState<UniqueIdentifier | null>(null);
  const [optimisticTasks, setOptimisticTasks] = React.useState<FlowTask[]>(tasks);

  const queryClient = useQueryClient();
  const updateTaskMutation = useUpdateTodoTask();

  React.useEffect(() => {
    if (shouldSyncOptimisticTasks(optimisticTasks, tasks)) {
      setOptimisticTasks(tasks);
    }
  }, [tasks]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: () => (isTextInputFocused() ? null : undefined),
    }),
  );

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveId(active.id);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveId(null);

    if (!over) {
      setOptimisticTasks(tasks);
      return;
    }
    const taskId = active.id;
    const activeTask = optimisticTasks.find((t) => String(t.uid) === String(taskId));
    if (!activeTask) return;

    const { newStatus, isReordering } = getDropInfo(over.id, activeTask, optimisticTasks);

    const resolvedStatus = newStatus ?? (over.data.current?.status as FlowTask["status"] | undefined);

    if (!resolvedStatus) {
      setOptimisticTasks(tasks);
      return;
    }

    let updated = [...optimisticTasks];
    const idx = updated.findIndex((t) => String(t.uid) === String(taskId));

    if (idx === -1) {
      setOptimisticTasks(tasks);
      return;
    }

    // Move between columns
    if (activeTask.status !== resolvedStatus && !isReordering) {
      updated[idx] = {
        ...updated[idx],
        status: resolvedStatus,
        updatedAt: new Date().toISOString(),
      };

      setOptimisticTasks(updated);

      if (!isPreviewMode) {
        updateTaskMutation.mutate({
          taskId: String(taskId),
          updates: { status: resolvedStatus },
        });
      }
      return;
    }

    // Reorder within same column
    if (isReordering && over.id !== taskId) {
      const statusTasks = updated.filter((t) => t.status === resolvedStatus);
      const oldIndex = statusTasks.findIndex((t) => String(t.uid) === String(taskId));
      const newIndex = statusTasks.findIndex((t) => String(t.uid) === String(over.id));

      if (oldIndex !== newIndex && newIndex !== -1 && oldIndex !== -1) {
        const reorderedStatusTasks = arrayMove(statusTasks, oldIndex, newIndex);
        const otherTasks = updated.filter((t) => t.status !== resolvedStatus);

        updated = [...otherTasks, ...reorderedStatusTasks];
        setOptimisticTasks(updated);
      } else {
        setOptimisticTasks(tasks);
      }
      return;
    }

    setOptimisticTasks(tasks);
  };
  const activeTask = activeId ? optimisticTasks.find((t) => String(t.uid) === String(activeId)) : null;
  const tasksByStatus = React.useMemo(() => getTasksByStatus(optimisticTasks), [optimisticTasks]);

  if (isLoading) return <KanbanLoadingState />;

  return (
    <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <Box sx={Sx.board}>
        {TASK_STATUSES.map((status) => (
          <DroppableColumn
            key={status}
            status={status}
            tasks={tasksByStatus[status] || []}
            statusColor={STATUS_COLORS[status]}
            onCreateTask={onCreateTask}
            isPreviewMode={isPreviewMode}
            isLimitReached={isLimitReached}
          />
        ))}
      </Box>
      <DragOverlay adjustScale={false} dropAnimation={null} zIndex={9999} modifiers={[snapCenterToCursor]}>
        {activeTask ? (
          <Box
            sx={{
              width: 280,
              minWidth: 280,
              opacity: 0.95,
              cursor: "grabbing",
              pointerEvents: "none",
              touchAction: "none",
              zIndex: 9999,
            }}
          >
            <FlowTaskCard task={activeTask} isPreviewMode={isPreviewMode} />
          </Box>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
